import TextInput from '@zoocasa/node-kit/components/controls/text-input';
import Button from 'components/control/button';
import FilterIcon from 'components/icon/filter-icon';
import { homepageIds, searchFilterIds, testIds } from 'constants/test-constants';
import { Filter } from 'contexts/preferences/listing-params/types';
import useOutsideClickHandler from 'hooks/use-outside-click-handler';
import { useIsMobile, useIsIpadMini } from 'hooks/use-size-class';
import { SEARCH_OPTION_HOME_APPRAISAL } from '@tenants/types/theme';
import { PartialDeep } from 'type-fest';
import { buildClassName } from 'utils/build-class-name';
import { searchOptions } from 'utils/select-options';
import { useSearchBar } from '../useSearchBar';
import SearchOptionButtonGroup from './search-option-button-group';
import styles from './style.module.scss';
import CloseIcon from 'components/icon/close-icon';
import React, { useRef, KeyboardEvent } from 'react';
import { useSearchBarTracking } from 'hooks/use-search-bar-tracking';
import { SearchAgentPrediction } from 'components/suggested-location-dropdown';
import type SearchPrediction from 'data/search-predictions';
import { useTranslation } from 'next-i18next';
import { LOCALE_SEARCH_PATHS, Namespaces } from 'types/locales';
import useDebounce from 'hooks/use-debounce';

export type AdvancedSearchProps = {
  showsSearchOptions?: boolean;
  isFilterButtonHidden?: boolean;
  isInPanel?: boolean;
  testId?: string;
  className?: string;
  searchWrapperClassName?: string;
  searchButtonClassName?: string;
  suggestedDropdownClassName?: string;
  hideSearchPanel?: () => void;
  onValueChange?: (filters: PartialDeep<Filter>, changedFilter?: string) => void;
  onFilterButtonClick?: () => void;
};

const AdvancedSearch = ({ showsSearchOptions = false, isInPanel = false, testId, isFilterButtonHidden = false, onFilterButtonClick, className, searchWrapperClassName, searchButtonClassName, suggestedDropdownClassName }: AdvancedSearchProps) => {
  const { t } = useTranslation(Namespaces.SEARCH);
  const {
    theme,
    isMobile,
    isLocationDropdownActive,
    SuggestedLocationDropdown,
    searchPlaceholder,
    isHomeAppraisal,
    isExpTenant,
    disablePopularSearches,
    currentSearchOptions,
    searchPredictions:{
      setActivePrediction,
      isLoadingSearchPredictions,
      searchPredictions,
      searchAgentPredictions,
      isTyping,
    },
    locationQuery,
    setLocationQuery,
    doOnSearchResultClicked,
    doOnSearchButtonClicked,
    doOnSearchOptionChanged,
    hideSearchSuggestions,
    doOnSearchBarClicked,
    hideSearchPanel,
    isHomePage,
    onSearchPage,
    waitForPredictionsToLoad,
  } = useSearchBar({ isInPanel });

  const isSmallDevice = useIsMobile();
  const isIpadMini = useIsIpadMini();
  const shouldRemoveBorders = !isExpTenant && (isSmallDevice || isIpadMini);
  const localTextInputRef = useRef<HTMLInputElement>(null);

  const { handleFocus, handleInput, handleBlur, markSearchAsSubmitted } = useSearchBarTracking({
    textInputRef: localTextInputRef,
    locationQuery,
    isHomePage,
    onSearchPage,
    isInPanel,
  });

  const debouncedDoOnSearchButtonClicked = useDebounce(() => doOnSearchButtonClicked(locationQuery), 400);

  const handleSearchButtonClick = async (searchPrediction?: SearchPrediction | SearchAgentPrediction) => {
    markSearchAsSubmitted();
    await waitForPredictionsToLoad();
    if (searchPrediction) {
      doOnSearchResultClicked(searchPrediction);
    } else {
      debouncedDoOnSearchButtonClicked();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearchButtonClick(); // Trigger the search submission logic, which also marks as submitted
    }
  };

  return (
    <div
      className={
        buildClassName(styles.component,
          isExpTenant && styles['exp-theme'] + ' ' + styles['fake-search'],
          isHomeAppraisal && styles['home-appraisal'],
          className)
      }
      data-testid={testId ? testId : isMobile ? testIds.advancedSearchMobile : homepageIds.homepageSearchbar}>
      {showsSearchOptions && theme.searchOptions.length>1 &&
      <div className={styles['search-options']}>
        <SearchOptionButtonGroup
          isExpTheme={isExpTenant}
          options={searchOptions(theme.searchOptions)}
          onValueChange={doOnSearchOptionChanged}
          value={currentSearchOptions}
        />
      </div>
      }
      <div
        ref={useOutsideClickHandler(hideSearchSuggestions)}
        className={buildClassName(
          styles['search-wrapper'],
          isExpTenant && styles['exp-wrapper'],
          shouldRemoveBorders && styles['no-borders'],
          searchWrapperClassName
        )}>
        <TextInput
          ref={localTextInputRef}
          name="search"
          placeholder={searchPlaceholder}
          value={locationQuery}
          onClick={doOnSearchBarClicked}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onValueChange={value => {
            setLocationQuery(value);
            handleInput(value);
          }}
          onKeyDown={handleKeyDown}
          autoComplete='off'
          autoFocus={isInPanel}
          data-isopen={isLocationDropdownActive.toString()}
          data-testid={isMobile ? testIds.mobileSearchField : homepageIds.homepageSearchField}
        />
        {SuggestedLocationDropdown &&
        <SuggestedLocationDropdown
          isLoading={isLoadingSearchPredictions}
          searchPredictions={searchPredictions}
          setActivePrediction={setActivePrediction}
          onClick={searchPrediction => {
            handleSearchButtonClick(searchPrediction);
          }}
          isActive={isLocationDropdownActive}
          className={buildClassName(styles['suggested-location-dropdown'], suggestedDropdownClassName)}
          isAdvancedSearch={true}
          locationQuery={locationQuery}
          isTyping={isTyping}
          disablePopularSearches={!!disablePopularSearches}
          searchAgentPredictions={searchAgentPredictions}
        />
        }
        {isMobile && isInPanel ?
          <Button label={<CloseIcon />} className={styles.close} theme="primary" onClick={hideSearchPanel}/>
          : <div className={styles['search-button-container']}>
            {!isFilterButtonHidden && currentSearchOptions !== SEARCH_OPTION_HOME_APPRAISAL &&
            <Button 
              label={t(LOCALE_SEARCH_PATHS.BAR_BUTTON_FILTER, { defaultValue: 'Filter' })} 
              Icon={FilterIcon} 
              theme="secondary" 
              onClick={onFilterButtonClick} 
              testId={searchFilterIds.searchbarFilterButton} 
            />
            }
            <Button
              label={t(LOCALE_SEARCH_PATHS.BAR_BUTTON_SEARCH, { defaultValue: 'Search' })}
              theme="primary"
              onClick={() => handleSearchButtonClick()}
              testId={isMobile ? testIds.mobileSearchButton : homepageIds.homepageSearchButton}
              className={searchButtonClassName}
            />
          </div>}
      </div>
    </div>
  );
};

export default AdvancedSearch;
